import { useCallback } from 'react';
import useTranslation from '~/hooks/useTranslation';
import participantAddressStyles from '~/styles/myEnvironment/participant-address.module.css';
import useStateOptions from '~/hooks/bookingFlow/useStateOptions';
import { useFlatCountryOptions } from '~/hooks/bookingFlow/useCountryOptions';
import type { ParticipantAddressType } from './Participant';

type Props = {
  className?: string;
  address: ParticipantAddressType;
};

export default function ParticipantAddress({
  className,
  address,
}: Props) {
  const translate = useTranslation();
  const statesOptions = useStateOptions();
  const countryOptions = useFlatCountryOptions();

  const stateLabelForCode = useCallback((code: string) => {
    return statesOptions.find((state) => state.value === code)?.label;
  }, [statesOptions]);

  const countryLabelForIso = useCallback((iso: string) => {
    return countryOptions.find((country) => country.value === iso.toLowerCase())?.label;
  }, [countryOptions]);

  return (
    <div className={className}>
      <h4 className={participantAddressStyles.participantAddressTitle}>
        {translate('myEnvironment.reservation.address_title')}
      </h4>

      <p>
        {`${translate('form.address_line_1')}: ${address?.street} ${address?.housenum || ''}`}
      </p>

      {address?.addressLine2 && (
        <p>
          {`${translate('form.address_line_2')}: ${address.addressLine2}`}
        </p>
      )}

      <p>
        {`${translate('form.city')}: ${address?.city}`}
      </p>

      {address?.state && (
        <p>
          {`${translate('form.state')}: ${stateLabelForCode(address.state) || address.state}`}
        </p>
      )}

      <p>
        {`${translate('form.postal_code')}: ${address?.postalCode}`}
      </p>

      <p>
        {`${translate('form.country')}: ${countryLabelForIso(address?.country)}`}
      </p>
    </div>
  );
}
